import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TypeDeleteDialog } from "@/components/TypeDeleteDialog";
import { ArchiveRestore, Trash2, Loader2 } from "lucide-react";
import { timeAgo } from "@/pages/admin/sections/shared";

// Reusable per-module Archived / All record view (Restore + Permanent Delete).
// Bulk operations live in the Records Center; this is the in-module read+restore
// surface every module inherits for its Archived/All tabs.
export function ArchivedPanel({ module, status = "archived", singular = "record", allowDelete = true, onChanged }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null); // { id }

  const load = useCallback(() => {
    setItems(null);
    api.get(`/lifecycle/${module}`, { params: { status } })
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [module, status]);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    setBusy(true);
    try { await api.post(`/lifecycle/${module}/${id}/restore`); toast.success("Record restored."); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    try { await api.post(`/lifecycle/${module}/${del.id}/delete`, { confirm: "DELETE" }); toast.success("Permanently deleted."); setDel(null); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gold" size={24} /></div>;
  if (items.length === 0) return <div className="bg-navy-800 border border-white/5 p-12 text-center text-gray-500 text-sm" data-testid={`archived-empty-${module}`}>No {status === "all" ? "" : status} {singular.toLowerCase()}s found.</div>;

  return (
    <div className="bg-navy-800 border border-white/5 overflow-x-auto" data-testid={`archived-panel-${module}`}>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-gray-500 border-b border-white/10">
          <th className="p-3 font-medium">{singular}</th>
          <th className="p-3 font-medium">Status</th>
          <th className="p-3 font-medium">Archive info</th>
          <th className="p-3 font-medium text-right">Actions</th>
        </tr></thead>
        <tbody>
          {items.map((it) => {
            const archived = it.lifecycle_status === "archived";
            return (
              <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`archived-row-${it.id}`}>
                <td className="p-3 text-white max-w-[280px] truncate">{it.name}</td>
                <td className="p-3"><span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${archived ? "bg-white/10 text-gray-400 border-white/20" : "bg-green-500/15 text-green-300 border-green-500/30"}`}>{archived ? "Archived" : "Active"}</span></td>
                <td className="p-3 text-gray-500 text-xs">{archived ? <span>{it.archived_by || "—"} · {it.archived_at ? timeAgo(it.archived_at) : ""}</span> : <span className="text-gray-600">—</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    {archived && <button onClick={() => restore(it.id)} disabled={busy} data-testid={`archived-restore-${it.id}`} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-white/10 text-gray-200 hover:border-gold/50 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"><ArchiveRestore size={13} /> Restore</button>}
                    {archived && allowDelete && <button onClick={() => setDel({ id: it.id })} disabled={busy} data-testid={`archived-delete-${it.id}`} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40"><Trash2 size={13} /> Delete</button>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <TypeDeleteDialog open={!!del} count={1} label={singular.toLowerCase()} onConfirm={doDelete} onCancel={() => setDel(null)} />
    </div>
  );
}

export default ArchivedPanel;
